// useAuthentication.js
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  loggedIn,
  updateIdToken,
  getUserProfile,
  loggedOut,
} from '../modules/user';
import { toggleAppSidebar } from '../modules/app';
import fire from '../config/fire';
import { authUtil } from '../utils';
import {
  ProxyEventTypes,
  useHostAppProxy,
} from 'src/contexts/HostAppProxyContext';

const useAuthentication = () => {
  const {
    settings: { customAuthToken, isWrapped },
    sendMessageToHost,
  } = useHostAppProxy();
  const dispatch = useDispatch();
  const idTokenRefreshTimer = useRef<NodeJS.Timeout | null>(null);
  const loggedInViaCustomToken = useRef<boolean>(false);

  useEffect(() => {
    const ct = customAuthToken;
    if (ct) {
      // Check if user is already signed in with the same account
      const currentUser = fire.auth().currentUser;
      if (currentUser) {
        // User is already signed in - verify if we need to sign in with custom token
        // If we're already signed in via custom token, don't sign in again
        if (loggedInViaCustomToken.current) {
          console.log('Already signed in with custom token, skipping');
          return;
        }
        // If user is signed in but not via custom token, we should sign in with custom token
        // to sync with native app
        console.log('User already signed in, signing in with custom token to sync');
      }
      
      console.log('Attempting to sign in with custom token');
      loggedInViaCustomToken.current = true;
      fire
        .auth()
        .signInWithCustomToken(ct)
        .then((userCredential: any) => {
          console.log('Successfully signed in with custom token');
          sendMessageToHost({
            type: ProxyEventTypes.USER_LOGGED_IN,
            data: {
              user: userCredential.user ? userCredential.user.uid : null,
            },
          });
        })
        .catch((error: any) => {
          console.error('Error signing in with custom token:', error);
          loggedInViaCustomToken.current = false;
          sendMessageToHost({
            type: ProxyEventTypes.MESSAGE,
            data: `Error signing in with custom token: ${error.message}`,
          });
          // If token is expired or invalid, request a new one
          if (error.code === 'auth/custom-token-mismatch' || 
              error.code === 'auth/argument-error') {
            sendMessageToHost({
              type: ProxyEventTypes.MESSAGE,
              data: 'Requesting new token due to auth error',
            });
          }
        });
    } else {
      loggedInViaCustomToken.current = false;
    }
  }, [customAuthToken, sendMessageToHost]);

  useEffect(() => {
    let active = true;
    let verificationRefreshInFlight = false;

    // A reader can verify in another tab while this app keeps its old Firebase
    // user and ID token. Refresh both when they return, including on book pages
    // that do not render the verification wall.
    const syncEmailVerification = async () => {
      const user = fire.auth().currentUser;
      if (
        !user ||
        user.isAnonymous ||
        user.emailVerified ||
        verificationRefreshInFlight
      ) {
        return;
      }
      verificationRefreshInFlight = true;
      try {
        await user.reload();
        if (
          !active ||
          fire.auth().currentUser?.uid !== user.uid ||
          !user.emailVerified
        ) {
          return;
        }
        const idToken = await user.getIdToken(true);
        if (!active || fire.auth().currentUser?.uid !== user.uid) return;
        dispatch(updateIdToken(idToken));
        dispatch(getUserProfile(idToken));
      } catch (error) {
        // A later focus or the verification wall will retry transient failures.
      } finally {
        verificationRefreshInFlight = false;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') syncEmailVerification();
    };
    window.addEventListener('focus', syncEmailVerification);
    document.addEventListener('visibilitychange', onVisibilityChange);

    const refreshIdToken = async () => {
      const idToken = await authUtil.getFreshIdToken();
      dispatch(updateIdToken(idToken));
    };

    const unsubscribeAuthStateChanged = fire.auth().onAuthStateChanged(
      async (user: any) => {
        if (user) {
          const idToken = await user.getIdToken().catch((err: any) => {
            console.error(err);
            return null;
          });
          
          if (!idToken) return;

          window.localStorage.setItem('br-storage-key', user.uid);
          window.localStorage.setItem('br-is-anonymous', user.isAnonymous.toString());
          dispatch(loggedIn(user, idToken));
          dispatch(getUserProfile(idToken));

          // Check if we're wrapped in native app - prioritize window.ReactNativeWebView check
          const isWrappedInNativeApp = typeof window !== 'undefined' && !!window.ReactNativeWebView;
          
          // Check if login was via OAuth by examining provider data
          // Email/password users have providerId === 'password'
          // OAuth providers have IDs like 'google.com', 'facebook.com', 'apple.com'
          const isOAuthProvider = user.providerData && user.providerData.length > 0 &&
            user.providerData.some((provider: any) =>
              provider.providerId !== 'password'
            );

          console.log('Auth state changed:', {
            isWrapped,
            isWrappedInNativeApp,
            hasReactNativeWebView: typeof window !== 'undefined' && !!window.ReactNativeWebView,
            loggedInViaCustomToken: loggedInViaCustomToken.current,
            isOAuthProvider,
            providerData: user.providerData?.map((p: any) => p.providerId),
            uid: user.uid
          });

          // If wrapped and not logged in via custom token and not OAuth provider,
          // this is likely an email/password login - notify native app
          if (isWrappedInNativeApp && !loggedInViaCustomToken.current && !isOAuthProvider) {
            console.log('✅ Detected email/password login in wrapped app, notifying native app');
            console.log('📤 Sending message with:', {
              type: ProxyEventTypes.USER_LOGGED_IN,
              user: user.uid,
              hasIdToken: !!idToken,
              loginType: 'email-password',
            });
            
            // Send message immediately - ReactNativeWebView should be available
            try {
              const message = {
                type: ProxyEventTypes.USER_LOGGED_IN,
                data: {
                  user: user.uid,
                  idToken: idToken,
                  loginType: 'email-password',
                },
              };
              const messageStr = JSON.stringify(message);
              console.log('📨 Posting message to ReactNativeWebView:', messageStr);
              window.ReactNativeWebView!.postMessage(messageStr);
              console.log('✅ Successfully sent USER_LOGGED_IN message to native app');
            } catch (error) {
              console.error('❌ Error sending USER_LOGGED_IN message:', error);
              // Fallback to sendMessageToHost
              try {
                console.log('🔄 Trying fallback sendMessageToHost');
                sendMessageToHost({
                  type: ProxyEventTypes.USER_LOGGED_IN,
                  data: {
                    user: user.uid,
                    idToken: idToken,
                    loginType: 'email-password',
                  },
                });
              } catch (fallbackError) {
                console.error('❌ Fallback also failed:', fallbackError);
              }
            }
          } else {
            console.log('⏭️ Not notifying native app:', {
              isWrappedInNativeApp,
              loggedInViaCustomToken: loggedInViaCustomToken.current,
              isOAuthProvider,
              reason: !isWrappedInNativeApp ? 'not wrapped' : 
                      loggedInViaCustomToken.current ? 'logged in via custom token' :
                      isOAuthProvider ? 'OAuth provider' : 'unknown'
            });
          }

          if (idTokenRefreshTimer.current) {
            clearInterval(idTokenRefreshTimer.current);
          }
          idTokenRefreshTimer.current = setInterval(
            refreshIdToken,
            60 * 5 * 1000
          );
        } else {
          loggedInViaCustomToken.current = false;
          window.localStorage.removeItem('br-storage-key');
          if (idTokenRefreshTimer.current) {
            clearInterval(idTokenRefreshTimer.current);
          }
          dispatch(loggedOut());
          dispatch(toggleAppSidebar(false));
        }
      },
      (error: any) => {
        console.error('onAuthStateChanged error:', error);
      }
    );

    const unsubscribeIdTokenChanged = fire.auth().onIdTokenChanged(
      (user: any) => {
        if (user) {
          refreshIdToken();
        }
      },
      (error: any) => {
        console.error('onIdTokenChanged error:', error);
      }
    );

    // Cleanup subscriptions on unmount
    return () => {
      active = false;
      window.removeEventListener('focus', syncEmailVerification);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      unsubscribeAuthStateChanged();
      unsubscribeIdTokenChanged();
      if (idTokenRefreshTimer.current) {
        clearInterval(idTokenRefreshTimer.current);
      }
    };
  }, [isWrapped, sendMessageToHost]);

  // signal to host app that the webapp is ready
  useEffect(() => {
    sendMessageToHost({
      type: ProxyEventTypes.WEB_APP_READY,
    });
  }, []);
};

export default useAuthentication;
